import { useCallback, useMemo, useState } from 'react';
import type { InviteCandidate, InviteTab, InviteTabKey } from '@tempmee/react-blocks';
import { useInviteProfessional } from '~/commonComponents/InviteProfessional/hooks';
import {
  fromProfessionalProfile,
  type IListItemData,
} from '~/components/ShiftRequestRedesign/AddInvites/EmbeddedInviteList/professionalAdapters';
import { SortInviteListsByAvailability } from '~/constants/ff.posthog';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useGetProfessionalProfiles } from '~/domain/Professionals/usecases/useGetProfessionalProfiles';
import type { ProfessionTypeEnum } from '~/models';
import { toInviteCandidate } from '../utils/inviteCandidateAdapters';
import {
  diffSelection,
  findUnpooledInvitedIds,
  normaliseInvitedIds,
  resolveProfessionCode,
  splitInvitedOut,
  toSourceRows,
  toTabs,
  withHydratedInvited,
  type TSourceTabKey,
} from '../utils/invitePools';

interface IUseInvitePoolsArgs {
  /** The open shift being staffed. Scopes the already-sent invites. */
  jobId: string;
  officeId?: string;
  /** The shift's profession — only professionals who hold it are offered. */
  professionType: string;
}

export interface IUseInvitePoolsResult {
  isLoading: boolean;
  isInviting: boolean;
  tabs: InviteTab[];
  activeTab: InviteTabKey;
  onTabChange: (key: InviteTabKey) => void;
  /** Rows for the active pool, already in the block's shape. */
  candidates: InviteCandidate[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Underlying row for a candidate id — what the Skills drill-in reads. */
  findRow: (id: string) => IListItemData | undefined;
  /** Clears the selection once an invite has gone out. */
  clearSelection: () => void;
}

/**
 * Feeds the `InviteCandidates` block for an open shift.
 *
 * Sources the pools from the shared `useInviteProfessional` hook — the same
 * fetches, filters, availability sorting and `professional_selected` analytics
 * every other invite surface uses — then bridges the two mismatches between that
 * hook and the redesigned block:
 *
 * - **A fourth tab the hook cannot hold.** It owns the active *source* pool, so
 *   `invited` is tracked here and layered over it, leaving its auto-pick of the
 *   first non-empty pool and the pool it attributes a selection to untouched.
 * - **Selection is a full list, not a toggle.** The block emits the whole next
 *   set; the hook takes add/remove.
 * - **An invite can outlive its pool.** The pools only hold professionals the
 *   office could still invite, so anyone already invited but no longer offered
 *   is fetched by id and added to `invited` — otherwise the shift's own invite
 *   would leave no trace on the page (GOTU-7381).
 *
 * The pool arithmetic itself is pure and lives in `./invitePools`.
 */
export const useInvitePools = ({
  jobId,
  officeId,
  professionType,
}: IUseInvitePoolsArgs): IUseInvitePoolsResult => {
  const allProfessions = useAppSelector((state) => state.user.allProfessions);
  const sortByAvailability = useFeatureFlag(SortInviteListsByAvailability);

  const getProfessionName = useCallback(
    (code: string) =>
      allProfessions?.find(({ code: candidate }: { code: string }) => candidate === code)?.name ??
      code,
    [allProfessions],
  );

  const professionCode = useMemo(() => resolveProfessionCode(professionType), [professionType]);

  const {
    activeTab: activeSourceTab,
    favoriteProfessionals,
    recentProfessionals,
    recommendedProfessionals,
    isLoading,
    isInviting,
    onSelectProfessionals,
    onTabChange: onSourceTabChange,
    selectedProfessionals,
    sentProfessionalIds,
  } = useInviteProfessional({
    isOpen: true,
    // The page already renders a loaded job; the invite panel must not block on a
    // second fetch of it.
    isRefetchJob: false,
    jobId,
    filterProfession: professionCode as ProfessionTypeEnum,
    officeId,
    onClosePopup: () => {},
    sortByAvailability,
  });

  const sourceRows = useMemo(
    () =>
      toSourceRows(
        {
          favorites: favoriteProfessionals,
          'worked-with': recentProfessionals,
          'new-to-you': recommendedProfessionals,
        },
        professionCode,
        getProfessionName,
      ),
    [
      favoriteProfessionals,
      recentProfessionals,
      recommendedProfessionals,
      professionCode,
      getProfessionName,
    ],
  );

  const invitedIds = useMemo(
    () => normaliseInvitedIds(sentProfessionalIds),
    [sentProfessionalIds],
  );

  // Invites whose professional no pool offers any more; fetched one by one
  // because the job payload carries the invite as a bare id and there is no
  // bulk professional endpoint. Normally empty, so no request is made at all.
  const unpooledInvitedIds = useMemo(
    () => findUnpooledInvitedIds(sourceRows, invitedIds),
    [sourceRows, invitedIds],
  );

  const { profiles: unpooledInvitedProfiles } = useGetProfessionalProfiles(unpooledInvitedIds, {
    officeId,
    jobProfession: professionCode,
  });

  const hydratedInvited = useMemo(
    () =>
      unpooledInvitedProfiles.map((profile) =>
        fromProfessionalProfile(profile, professionCode, getProfessionName),
      ),
    [unpooledInvitedProfiles, professionCode, getProfessionName],
  );

  /** The four pools the switcher offers, with the invited ones split out. */
  const rowsByTab = useMemo(
    () => withHydratedInvited(splitInvitedOut(sourceRows, invitedIds), hydratedInvited),
    [sourceRows, invitedIds, hydratedInvited],
  );

  // `useInviteProfessional` only knows the three source pools, so `invited` is
  // held here and takes precedence while it is on.
  const [isInvitedTabActive, setIsInvitedTabActive] = useState(false);
  const activeTab: InviteTabKey = isInvitedTabActive
    ? 'invited'
    : (activeSourceTab as TSourceTabKey);

  const onTabChange = useCallback(
    (key: InviteTabKey) => {
      setIsInvitedTabActive(key === 'invited');
      if (key !== 'invited') {
        onSourceTabChange(key as Parameters<typeof onSourceTabChange>[0]);
      }
    },
    [onSourceTabChange],
  );

  const rows = rowsByTab[activeTab];
  const tabs = useMemo(() => toTabs(rowsByTab), [rowsByTab]);
  const candidates = useMemo(() => rows.map(toInviteCandidate), [rows]);
  const selectedIds = useMemo(() => [...selectedProfessionals], [selectedProfessionals]);

  const onSelectionChange = useCallback(
    (next: string[]) => {
      const visibleIds = new Set(rows.map((row) => row.id));
      const { added, removed } = diffSelection(selectedProfessionals, next, visibleIds);

      if (added.length > 0) {
        onSelectProfessionals(added, true);
      }
      if (removed.length > 0) {
        onSelectProfessionals(removed, false);
      }
    },
    [onSelectProfessionals, rows, selectedProfessionals],
  );

  const findRow = useCallback(
    (id: string) =>
      (Object.keys(rowsByTab) as InviteTabKey[])
        .flatMap((key) => rowsByTab[key])
        .find((row) => row.id === id),
    [rowsByTab],
  );

  const clearSelection = useCallback(
    () => onSelectProfessionals([...selectedProfessionals], false),
    [onSelectProfessionals, selectedProfessionals],
  );

  return {
    isLoading,
    isInviting,
    tabs,
    activeTab,
    onTabChange,
    candidates,
    selectedIds,
    onSelectionChange,
    findRow,
    clearSelection,
  };
};
